/**
 * LiveGate
 * ------------------------------------------------------------------
 * Wraps a tab view so the real content is only rendered once Tesla
 * OAuth has completed AND vehicle data has merged. Keeps the "no mock
 * data in the app" promise on every tab (AI, Range, Trip, More).
 *
 * Three states:
 *  1. No Tesla session      → SignInPromptCard
 *  2. Session but no data   → shimmering skeleton
 *  3. Live data available   → children (the real view)
 *
 * Home has its own bespoke skeleton because its layout is unique.
 */

import React from "react";
import { Link } from "react-router-dom";
import { BatteryCharging, ArrowRight } from "lucide-react";
import { useVehicle } from "../../../lib/vehicleContext";

const shimmer =
  "bg-gradient-to-r from-white/[0.03] via-white/[0.06] to-white/[0.03] bg-[length:200%_100%] animate-[shimmer_1.6s_linear_infinite]";

const SignInPromptCard = ({ tab }) => (
  <div
    data-testid="live-gate-signin-prompt"
    className="pt-4 space-y-4"
  >
    <div
      className="rounded-2xl border p-6 text-center"
      style={{
        borderColor: "rgba(57,255,20,0.35)",
        background:
          "linear-gradient(180deg, rgba(57,255,20,0.06), rgba(0,229,255,0.04) 60%, transparent)",
      }}
    >
      <div
        className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center border"
        style={{
          background: "rgba(57,255,20,0.1)",
          borderColor: "rgba(57,255,20,0.5)",
        }}
      >
        <BatteryCharging
          className="w-7 h-7"
          style={{ color: "#39FF14" }}
          strokeWidth={2}
        />
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-3">
        EVIQ Intelligence™
      </div>
      <div className="font-display text-lg text-white font-semibold leading-tight mt-1">
        Sign in with Tesla to unlock{" "}
        <span className="brand-text-gradient">
          {tab === "ai"
            ? "your AI battery narrative"
            : tab === "range"
            ? "your real range confidence"
            : tab === "trip"
            ? "your trip planner"
            : "your battery tools"}
        </span>
      </div>
      <p className="text-[12px] text-zinc-400 mt-2 leading-relaxed">
        This tab uses live pack telemetry from the Tesla Fleet API. No demo
        data — your dashboard only fills in once we&rsquo;re connected to your
        vehicle.
      </p>
      <Link
        to="/app"
        state={{ scrollTo: "tesla-signin" }}
        className="mt-4 w-full brand-gradient text-black font-semibold rounded-xl py-3 text-sm inline-flex items-center justify-center gap-2"
        data-testid="live-gate-signin-cta"
      >
        Go to Sign-in
        <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
      </Link>
    </div>
  </div>
);

const TabSkeleton = () => (
  <div className="pt-4 space-y-4" data-testid="live-gate-skeleton">
    <div className={`h-4 w-40 rounded ${shimmer}`} />
    <div className={`h-6 w-3/4 rounded ${shimmer}`} />
    <div className={`h-3 w-1/2 rounded ${shimmer}`} />
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
      <div className={`h-3 w-24 rounded ${shimmer}`} />
      <div className={`h-24 w-full rounded ${shimmer}`} />
    </div>
    <div className="grid grid-cols-2 gap-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className={`h-2 w-16 rounded ${shimmer}`} />
          <div className={`h-6 w-20 mt-2 rounded ${shimmer}`} />
          <div className={`h-2 w-14 mt-2 rounded ${shimmer}`} />
        </div>
      ))}
    </div>
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-2">
      <div className={`h-3 w-32 rounded ${shimmer}`} />
      <div className={`h-3 w-full rounded ${shimmer}`} />
      <div className={`h-3 w-11/12 rounded ${shimmer}`} />
      <div className={`h-3 w-9/12 rounded ${shimmer}`} />
    </div>
  </div>
);

export const LiveGate = ({ tab, children }) => {
  const { vehicle, live } = useVehicle();

  // Fully connected: render the real view unchanged.
  if (vehicle?.live) return children;

  // Session exists but data hasn't merged yet — shimmer.
  if (live?.connected) return <TabSkeleton />;

  // No Tesla session at all — sign-in prompt. This is intentional even
  // for owner-mode / founding members: the tabs behind LiveGate all
  // consume battery telemetry (SoH, VIN, odometer, GPS) that only makes
  // sense against a real Tesla connection. Owner / founding overrides
  // apply to the paywall, not to the "no mock data" contract.
  return <SignInPromptCard tab={tab} />;
};
